'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Client } from '@/core/domain';
import type { ClientWithServices } from '../../domain/dto/client-with-services';
import type { GetUserUidUseCase } from '@/features/authentication/domain/usecases/get-user-uid';
import type { GetClientsWithServicesByUserIdUseCase } from '../../domain/usecases/get-clients-with-services-by-user-id';

export interface ClientState {
  /** Clientes com serviços e próxima manutenção (fonte principal). */
  clientsWithServices: ClientWithServices[];
  /** Lista simples de clientes (derivada), para compatibilidade com RegisterServiceSection e LembretesEnviadosSection. */
  clients: Client[];
  loading: boolean;
  error: string | null;
}

export interface ClientEvents {
  /** Carrega clientes do usuário com serviços e próxima manutenção. */
  getByUserId: () => Promise<ClientWithServices[]>;
  clearError: () => void;
}

export interface ClientContextValue {
  state: ClientState;
  events: ClientEvents;
}

export interface ClientProviderProps {
  children: React.ReactNode;
  getUserUidUseCase: GetUserUidUseCase;
  getClientsWithServicesByUserIdUseCase: GetClientsWithServicesByUserIdUseCase;
}

const initialState: ClientState = {
  clientsWithServices: [],
  clients: [],
  loading: false,
  error: null,
};

const ClientContext = createContext<ClientContextValue | null>(null);

export function ClientProvider({
  children,
  getUserUidUseCase,
  getClientsWithServicesByUserIdUseCase,
}: ClientProviderProps) {
  const [state, setState] = useState<ClientState>(initialState);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const getUserUid = useCallback(async (): Promise<string | null> => {
    const userId = await getUserUidUseCase.execute();
    if (!userId) {
      setState((s) => ({ ...s, error: 'Usuário não autenticado.', clientsWithServices: [], clients: [] }));
      return null;
    }
    return userId;
  }, [getUserUidUseCase]);

  const getByUserId = useCallback(
    async (): Promise<ClientWithServices[]> => {
      const userId = await getUserUid();
      if (!userId) return [];
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const list = await getClientsWithServicesByUserIdUseCase.execute(userId);
        const clients = list.map((cws) => cws.client);
        setState((s) => ({ ...s, clientsWithServices: list, clients }));
        return list;
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage, clientsWithServices: [], clients: [] }));
        return [];
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, getClientsWithServicesByUserIdUseCase]
  );

  const events: ClientEvents = useMemo(
    () => ({
      getByUserId,
      clearError,
    }),
    [getByUserId, clearError]
  );

  const value: ClientContextValue = useMemo(
    () => ({ state, events }),
    [state, events]
  );

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
}

export function useClients(): ClientContextValue {
  const ctx = useContext(ClientContext);
  if (!ctx) {
    throw new Error('useClients must be used within ClientProvider');
  }
  return ctx;
}
